/**
 * Punkt 115 (Paket 3 „Zustand bleibt erhalten“): Suche und Filter einer Liste stehen in der
 * Adresse statt nur im Seitenzustand — nach „Zurück“ vom Vorgang sind sie wieder da, und ein
 * geteilter Link zeigt dieselbe Ansicht. (Die Sortierung steht schon per `useListSort` dort.)
 *
 * - Ein Hook je Seite für alle ihre Schlüssel: mehrere Änderungen in einem Klick („Filter
 *   aufheben“ setzt drei Felder) landen so in EINEM Adress-Schreibvorgang statt sich gegenseitig
 *   zu überschreiben.
 * - Werte gleich der Vorgabe fehlen in der Adresse (`/kunden` statt `/kunden?typ=all`).
 * - Immer `replace` (kein Verlaufseintrag je Tastendruck oder Reiterklick); getippte Schlüssel
 *   (`entprellt`, Vorgabe `q`) gehen entprellt in die Adresse, die Anzeige folgt sofort.
 * - Ändert sich die Adresse von außen (Link auf dieselbe Seite, Zurück), gewinnt sie.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export function useUrlZustand<T extends Record<string, string>>(
  vorgabe: T,
  { entprellt = ["q"], ms = 300 }: { entprellt?: readonly string[]; ms?: number } = {},
): [T, (teil: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();
  const schluessel = Object.keys(vorgabe);
  const ausUrl = () => Object.fromEntries(schluessel.map((k) => [k, params.get(k) ?? vorgabe[k]])) as T;
  const kennungUrl = schluessel.map((k) => params.get(k) ?? "").join("\u0000");
  const kennung = (w: T) => schluessel.map((k) => (w[k] === vorgabe[k] ? "" : w[k])).join("\u0000");

  const [werte, setWerte] = useState<T>(ausUrl);
  const werteRef = useRef(werte);
  const geschrieben = useRef(kennungUrl);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Der Setter aus DIESEM Render: seine Funktionsform sieht die aktuellen Parameter (react-router
  // 6 reicht die aus dem Render durch, nicht die neuesten) — auch aus einem Timer heraus.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  useEffect(() => {
    if (kennungUrl === geschrieben.current) return;
    clearTimeout(timer.current);
    geschrieben.current = kennungUrl;
    const w = ausUrl();
    werteRef.current = w;
    setWerte(w);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kennungUrl]);
  useEffect(() => () => clearTimeout(timer.current), []);

  function schreibe(w: T) {
    geschrieben.current = kennung(w);
    setParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of schluessel) {
          if (w[k] === vorgabe[k] || w[k] === "") next.delete(k);
          else next.set(k, w[k]!);
        }
        return next;
      },
      { replace: true },
    );
  }

  function setze(teil: Partial<T>) {
    const neu = { ...werteRef.current, ...teil };
    werteRef.current = neu;
    setWerte(neu);
    clearTimeout(timer.current);
    if (Object.keys(teil).every((k) => entprellt.includes(k))) timer.current = setTimeout(() => schreibe(neu), ms);
    else schreibe(neu);
  }

  return [werte, setze];
}
